import React, { useState } from 'react';
import { UserFiltersState, ExportOptions } from '../types';
import { UserManagementService } from '../services/UserManagementService';
import './UserExport.css';

interface UserExportProps {
  filters: UserFiltersState;
}

export const UserExport: React.FC<UserExportProps> = ({ filters }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asyncMessage, setAsyncMessage] = useState<string | null>(null);

  const service = UserManagementService.getInstance();

  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true);
    setError(null);
    setAsyncMessage(null);

    const options: ExportOptions = {
      format,
      filters: {
        search: filters.search,
        authMethod: filters.authMethod,
        status: filters.status,
      },
    };

    try {
      const blob = await service.exportUsers(options);

      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const filterParts: string[] = [];
      if (filters.search) filterParts.push(`search-${filters.search}`);
      if (filters.authMethod !== 'all') filterParts.push(`auth-${filters.authMethod}`);
      if (filters.status !== 'all') filterParts.push(`status-${filters.status}`);
      const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
      const filename = `users_export_${dateStr}${filterSuffix}.${format}`;

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setShowDialog(false);
    } catch (err: any) {
      if (err.message?.startsWith('ASYNC_EXPORT:')) {
        const data = JSON.parse(err.message.replace('ASYNC_EXPORT:', ''));
        setAsyncMessage(`Export is being processed asynchronously (${data.recordCount} records). Estimated time: ${data.estimatedTime}`);
      } else {
        setError('Failed to export users. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="user-export">
      <button
        className="user-export__btn"
        onClick={() => setShowDialog(true)}
        aria-label="Export users"
      >
        📥 Export
      </button>

      {showDialog && (
        <div className="user-export__overlay" onClick={() => !exporting && setShowDialog(false)}>
          <div className="user-export__dialog" onClick={(e) => e.stopPropagation()}>
            <h4 className="user-export__dialog-title">Export Users</h4>
            <p className="user-export__dialog-desc">
              Export the currently filtered user data. Choose a format:
            </p>

            {error && <div className="user-export__error">{error}</div>}
            {asyncMessage && <div className="user-export__async">{asyncMessage}</div>}

            <div className="user-export__format-options">
              <button
                className="user-export__format-btn"
                onClick={() => handleExport('csv')}
                disabled={exporting}
              >
                📄 CSV
              </button>
              <button
                className="user-export__format-btn"
                onClick={() => handleExport('json')}
                disabled={exporting}
              >
                📋 JSON
              </button>
            </div>

            {exporting && <div className="user-export__progress">Exporting...</div>}

            <button
              className="user-export__cancel-btn"
              onClick={() => setShowDialog(false)}
              disabled={exporting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
